import { useMemo } from 'react'
import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { EmissionTier, MetricState } from '../../common'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import { useCurrentUser } from '../../../../hooks/users'

type FloodLossPoint = { x: number; y: number }

function formatCurrency(value: number | null | undefined, currencySymbol: string) {
  if (value == null || Number.isNaN(value)) return '–'
  return `${currencySymbol}${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

export default function FloodDamageLoss({
  propertyId,
  emissionTier,
}: {
  propertyId: number
  emissionTier: EmissionTier
}) {
  const { currencySymbol } = useCurrentUser()
  const { data, isLoading, error } = usePropertyClimateData('fld:damage_loss', { propertyId })

  const tierData = data?.data?.data?.[emissionTier] as
    | Record<string, Record<string, Record<string, FloodLossPoint[]>>>
    | undefined

  const { valueByRpAndYear, returnPeriods, years } = useMemo(() => {
    const store: Record<string, Record<number, number>> = {}
    const rpSet = new Set<string>((data?.data?.keys?.return_period as string[] | undefined) || [])
    const yearSet = new Set<number>()

    if (tierData) {
      Object.values(tierData).forEach(targets => {
        if (!targets || typeof targets !== 'object') return
        Object.values(targets as Record<string, Record<string, FloodLossPoint[]>>).forEach(rpMap => {
          if (!rpMap || typeof rpMap !== 'object') return
          Object.entries(rpMap).forEach(([rp, series]) => {
            rpSet.add(rp)
            if (!Array.isArray(series)) return
            series.forEach(point => {
              if (typeof point?.x !== 'number' || typeof point?.y !== 'number') return
              const year = Math.round(point.x)
              if (!store[rp]) store[rp] = {}
              store[rp][year] = (store[rp][year] ?? 0) + point.y
              yearSet.add(year)
            })
          })
        })
      })
    }

    return {
      valueByRpAndYear: store,
      returnPeriods: Array.from(rpSet).sort((a, b) => Number(a) - Number(b)),
      years: Array.from(yearSet).sort((a, b) => a - b),
    }
  }, [data?.data?.keys?.return_period, tierData])

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={120}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Could not load damage loss data.</Alert>
  }

  if (!data) {
    return <Alert severity="info">No damage loss data available for this property.</Alert>
  }

  if (data.state.value !== MetricState.SUCCEEDED) {
    return <Alert severity="warning">Damage loss calculations are not available (state: {data.state.value}).</Alert>
  }

  if (!returnPeriods.length || !years.length) {
    return <Alert severity="info">Damage loss data is empty for this property.</Alert>
  }

  return (
    <Box>
      <Box
        mb={2}
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}>
        <Box>
          <Typography variant="h6">Annual Losses</Typography>
          <Typography variant="body2" color="text.secondary">
            Loss estimates for different flood return periods
          </Typography>
        </Box>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Return period</TableCell>
            {years.map(col => (
              <TableCell key={col} align="right">
                <Typography variant="body2" color="text.secondary">
                  {col}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {returnPeriods.map(rp => (
            <TableRow key={rp}>
              <TableCell component="th" scope="row">
                {`${rp}-year event`}
              </TableCell>
              {years.map(year => {
                const value = valueByRpAndYear[rp]?.[year] ?? null
                return (
                  <TableCell key={`${rp}-${year}`} align="right">
                    {formatCurrency(value, currencySymbol)}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}
